import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';

import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { TabItemComponent } from './tab-item/tab-item.component';
import { TabButtonComponent } from './tab-item/tab-button.component';
import { filter, tap } from 'rxjs/operators';

@Component({
    selector: 'tab-nav',
    templateUrl: './tabs.component.html',
    styleUrls: ['./tabs.component.scss'],
})
export class TabsComponent implements OnInit, OnChanges {
    @Input('heading') heading: string;
    @Input() state: boolean;
    @Input() subHeading: boolean;
    @Input() slim: boolean = false;
    // This can be supplied if you wish to force a particular tab to be active
    @Input() override: string;
    @Input() tabsClass: string = '';
    @Input() wrapClass: string = '';

    @Output() status: EventEmitter<string> = new EventEmitter<string>();
    @Output() clickToggle: EventEmitter<boolean> = new EventEmitter<boolean>();

    tabs: TabItemComponent[] = [];
    buttons: TabButtonComponent[] = [];

    constructor(private _router: Router) {}

    ngOnInit() {
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                tap(() => this.checkActive())
            )
            .subscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // When a parent drives the active tab via [override] (e.g. the Time Value
        // report type), re-evaluate which tab is active as soon as that value
        // changes. Without this the active highlight only updated on
        // NavigationEnd, which races with change detection and left the previous
        // tab highlighted until a second click. Safe for tab-navs that don't use
        // [override] — the change never fires for them. (First change is a no-op:
        // tabs aren't registered until ngAfterContentInit, which also calls it.)
        if (changes['override'] && this.tabs.length) {
            this.checkActive();
        }
    }

    addTab(tab: TabItemComponent) {
        this.tabs.push(tab);
    }

    ngAfterContentInit() {
        this.checkActive();
    }

    checkActive() {
        let activeTab = null;

        this.tabs.forEach((tab) => {
            tab.active = false;
            if (this.override) {
                // Use exact equality when an override value is supplied — substring
                // matching against a synthetic URL would cause e.g. 'LiveArchived'
                // to also highlight the 'Live' tab.
                if (this.override === tab.click) {
                    activeTab = tab;
                    tab.active = true;
                }
            } else {
                const url = window.location.href;
                if (url.indexOf(`/${tab.click}`) > -1) {
                    activeTab = tab;
                    tab.active = true;
                }
            }
        });

        // Default to first tab
        if (this.tabs.length && !activeTab) {
            this.tabs[0].active = true;
        }
    }

    addButton(button: TabButtonComponent) {
        this.buttons.push(button);
    }

    buttonClick(event) {
        if (!event) return;

        // Provides a boolean click event on tab button for parent components e.g. modal trigger
        this.state = !this.state;
        this.clickToggle.emit(this.state);
    }

    buttonLink(link) {
        if (!link) return;

        this._router.navigateByUrl(link);
    }

    selectTab(tab: TabItemComponent) {
        // Provides alternative to content within "tab-item", used to emits status to parent commonly used to load component
        if (tab.click) {
            this.status.emit(tab.click);
        }
        if (tab.link) {
            this._router.navigate(tab.link);
        }

        this.tabs.forEach((tab) => (tab.active = false));
        tab.active = true;
    }
}
